import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, getOrganizationSchema, getBreadcrumbSchema } from '../../utils/seo';

export default function Contact() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  useSEO({
    title: 'İletişim Isparta Yıldız & Başyiğit Hukuk Danışmanlık',
    description: 'Isparta Yıldız & Başyiğit Hukuk ve Danışmanlık ile iletişime geçin. Adres: Çelebiler Mah. 119. Cad. No: 1 D: 6 Merkez/ISPARTA. Telefon: 0505 605 58 45, 0554 116 69 76. E-posta: info@ybhukukdanismanlik.com. Hukuki danışmanlık ihtiyaçlarınız için bize ulaşın, 24 saat içinde size geri dönüş yapıyoruz.',
    keywords: 'Isparta hukuk bürosu iletişim, avukat randevu, hukuki danışmanlık iletişim, Isparta avukat telefon',
    ogImage: 'https://public.readdy.ai/ai/img_res/76fd5baa8358c090ee5d3d5b0efc1628.jpg',
    canonical: `${siteUrl}/contact`,
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        getOrganizationSchema(),
        getBreadcrumbSchema([
          { name: 'Ana Sayfa', url: siteUrl },
          { name: 'İletişim', url: `${siteUrl}/contact` }
        ]),
        {
          '@type': 'ContactPage',
          '@id': `${siteUrl}/contact#contactpage`,
          url: `${siteUrl}/contact`,
          name: 'İletişim - Yıldız & Başyiğit Hukuk ve Danışmanlık'
        },
        {
          '@type': 'LocalBusiness',
          '@id': `${siteUrl}/#localbusiness`,
          name: 'Yıldız & Başyiğit Hukuk ve Danışmanlık',
          image: 'https://readdy.ai/api/search-image?query=Professional%20law%20office%20building%20exterior%20in%20Isparta%20Turkey&width=800&height=600&seq=office-building&orientation=landscape',
          '@id': `${siteUrl}/#organization`,
          url: siteUrl,
          telephone: ['+90 505 605 58 45', '+90 554 116 69 76'],
          email: 'info@ybhukukdanismanlik.com',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Çelebiler Mah. 119. Cad. No: 1 D: 6',
            addressLocality: 'Isparta',
            addressRegion: 'Isparta',
            postalCode: '32100',
            addressCountry: 'TR'
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 37.7648,
            longitude: 30.5566
          },
          openingHoursSpecification: [
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
              opens: '09:00',
              closes: '18:00'
            },
            {
              '@type': 'OpeningHoursSpecification', 
              dayOfWeek: 'Saturday',
              opens: '09:00',
              closes: '13:00'
            },
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: 'Sunday',
              opens: '00:00',
              closes: '00:00',
              validFrom: '2024-01-01',
              validThrough: '2024-12-31'
            }
          ],
          priceRange: '$$',
          currenciesAccepted: 'TRY'
        }
      ]
    }
  });

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const contactInfo = [
    {
      icon: 'ri-phone-line',
      title: 'Telefon',
      info: '0505 605 58 45',
      subInfo: '0554 116 69 76',
      link: 'tel:05056055845'
    },
    {
      icon: 'ri-mail-line',
      title: 'E-posta',
      info: 'info@ybhukukdanismanlik.com',
      subInfo: '24 saat içinde yanıt',
      link: 'mailto:info@ybhukukdanismanlik.com'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Adres',
      info: 'Çelebiler Mah. 119. Cad. No : 1 D : 6',
      subInfo: 'Merkez / ISPARTA',
      link: '#'
    }
  ];

  const officeHours = [
    { day: 'Pazartesi - Cuma', hours: '09:00 - 18:00' },
    { day: 'Cumartesi', hours: '09:00 - 13:00' },
    { day: 'Pazar', hours: 'Kapalı' }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    // Validate required fields
    if (!formData.name || !formData.email || !formData.subject) {
      setSubmitMessage('Lütfen tüm zorunlu alanları doldurun.');
      setIsSubmitting(false);
      return;
    }

    // Validate message length
    if (formData.message && formData.message.length > 500) {
      setSubmitMessage('Mesaj 500 karakterden fazla olamaz.');
      setIsSubmitting(false);
      return;
    }

    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('message', formData.message);

      await fetch('https://readdy.ai/api/form/d3ipahr2p8nb8r4n7j50', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      // Success message
      setSubmitMessage('Mesajınız başarıyla gönderildi. En kısa sürede size dönüş yapacağız.');
      setFormData({
        name: '',
        phone: '',
        email: '',
        subject: '',
        message: ''
      });

    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitMessage('Bir hata oluştu. Lütfen tekrar deneyin.');
    }

    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white py-20">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=Modern%20law%20office%20reception%20area%20with%20professional%20lighting%2C%20elegant%20furniture%2C%20contemporary%20design%2C%20clean%20white%20and%20dark%20color%20scheme%2C%20professional%20legal%20environment%2C%20high-quality%20interior%20photography%2C%20welcoming%20atmosphere&width=1920&height=600&seq=contact-hero-main&orientation=landscape')`
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-900/70"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 animate-fade-in-up font-poppins">
            İletişim
          </h1>
          <div className="w-24 h-1 bg-white mx-auto mb-6"></div>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto animate-fade-in-up font-poppins font-light" style={{ animationDelay: '0.2s' }}>
            Hukuki danışmanlık ihtiyaçlarınız için bizimle iletişime geçin
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {contactInfo.map((item, index) => (
              <div 
                key={index} 
                className="text-center p-8 bg-slate-50 rounded-lg hover:shadow-lg transition-all duration-300 animate-fade-in-up hover-lift"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${item.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 font-poppins">{item.title}</h3>
                {item.link !== '#' ? (
                  <a 
                    href={item.link}
                    className="text-slate-700 font-semibold mb-2 block hover:text-slate-900 transition-colors cursor-pointer font-poppins"
                  >
                    {item.info}
                  </a>
                ) : (
                  <p className="text-slate-700 font-semibold mb-2 font-poppins">{item.info}</p>
                )}
                <p className="text-slate-500 text-sm font-poppins font-light">{item.subInfo}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="animate-slide-in-left">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 font-poppins">
                Bize Mesaj Gönderin
              </h2>
              <form id="iletisim-formu" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2 font-poppins">Ad Soyad *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-slate-900 font-poppins font-light"
                      placeholder="Adınız ve soyadınız"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2 font-poppins">Telefon</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-slate-900 font-poppins font-light"
                      placeholder="Telefon numaranız"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2 font-poppins">E-posta *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-slate-900 font-poppins font-light"
                    placeholder="E-posta adresiniz"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2 font-poppins">Konu *</label>
                  <select 
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-slate-900 pr-8 font-poppins font-light"
                    required
                  >
                    <option value="">Konu seçiniz</option>
                    <option value="ticaret">Ticaret ve Şirketler Hukuku</option>
                    <option value="gayrimenkul">Gayrimenkul ve İnşaat Hukuku</option>
                    <option value="is">İş Hukuku ve Sosyal Güvenlik</option>
                    <option value="fikri">Fikri ve Sınai Mülkiyet Hukuku</option>
                    <option value="ceza">Ceza Hukuku (Beyaz Yaka Suçları)</option>
                    <option value="icra">İcra ve İflas Hukuku</option>
                    <option value="sozlesme">Sözleşmeler Hukuku</option>
                    <option value="aile">Aile Hukuku</option>
                    <option value="idare">İdare ve Vergi Hukuku</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2 font-poppins">Mesaj</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-slate-900 resize-none font-poppins font-light"
                    placeholder="Mesajınızı yazın... (Maksimum 500 karakter)"
                  ></textarea>
                  <div className="text-right text-xs text-slate-500 mt-1 font-poppins">
                    {formData.message.length}/500
                  </div>
                </div>

                {submitMessage && (
                  <div className={`p-3 rounded-lg text-sm font-poppins ${
                    submitMessage.includes('başarıyla') 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {submitMessage}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-slate-900 text-white py-3 px-6 rounded-lg font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50 whitespace-nowrap font-poppins cursor-pointer"
                >
                  {isSubmitting ? 'Gönderiliyor...' : 'Mesaj Gönder'}
                </button>
              </form>
            </div>

            {/* Office Hours & Map */}
            <div className="animate-slide-in-right">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 font-poppins">
                Çalışma Saatleri
              </h2>
              <div className="bg-slate-50 rounded-lg p-6 mb-8">
                <div className="space-y-4">
                  {officeHours.map((schedule, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className="font-medium text-slate-700 font-poppins">{schedule.day}</span>
                      <span className="text-slate-600 font-poppins font-light">{schedule.hours}</span>
                    </div>
                  ))}
                </div>
              </div>

              <h3 className="text-xl font-bold text-slate-900 mb-4 font-poppins">Konum</h3>
              <div className="rounded-lg overflow-hidden shadow-lg">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3151.835434509374!2d30.5566!3d37.7648!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14c73c5b7b5b5b5b%3A0x5b5b5b5b5b5b5b5b!2sIsparta%2C%20Turkey!5e0!3m2!1sen!2str!4v1234567890123"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Hukuk Bürosu Konum"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-6 animate-fade-in-up font-poppins">
            Neden Bizi Tercih Etmelisiniz?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="animate-fade-in-up">
              <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2 font-poppins">Hızlı Yanıt</h3>
              <p className="text-slate-600 font-poppins font-light">24 saat içinde size geri dönüş yapıyoruz</p>
            </div>
            <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2 font-poppins">Güvenilir Hizmet</h3>
              <p className="text-slate-600 font-poppins font-light">4+ yıllık deneyimimizle güvenilir çözümler</p>
            </div>
            <div className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-heart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2 font-poppins">Kişisel Yaklaşım</h3>
              <p className="text-slate-600 font-poppins font-light">Her müvekkilimize özel çözüm üretiyoruz</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
